// src/components/TagEditor.tsx
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Select, FormControl, InputLabel, FormHelperText,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, Switch, FormControlLabel,
  Slider, Accordion, AccordionSummary, AccordionDetails, Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { Tag, TagCategory, TagRegistry, PathStyle, MarkerStyle } from '../types/position.ts';
import { createTag, updateTag } from '../utils/tagsApi.ts';
import { dashArrayFor, flattenTagTree, parentProblem, resolveStyleDetail, withPlacedTag } from '../utils/tags.ts';
import type { DefaultStyles, StyleOrigin } from '../utils/tags.ts';
import MarkerShape from './MarkerShape.tsx';

const ROOT = 'root';

const originCaption: Record<StyleOrigin, string> = {
  own: '',
  inherited: 'Inherited',
  shaded: 'Calculated',
  default: 'Default',
};

// Sets or removes one key without mutating the source object.
function withField<T extends object>(style: T, key: keyof T, value: T[keyof T] | undefined): T {
  if (value === undefined) {
    return Object.fromEntries(Object.entries(style).filter(([k]) => k !== String(key))) as T;
  }
  return { ...style, [key]: value };
}

// Distinct reasons why some candidate parents (or the root) are unavailable.
export function parentReasons(candidates: Tag[], moving: Tag[], registry: TagRegistry): string[] {
  const reasons = new Set<string>();
  const root = parentProblem(null, moving, registry);
  if (root) reasons.add(root);
  for (const tag of candidates) {
    const problem = parentProblem(tag, moving, registry);
    if (problem) reasons.add(problem);
  }
  return Array.from(reasons);
}

const FieldFooter: React.FC<{ own: boolean; caption: string; onReset: () => void }> = ({ own, caption, onReset }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: 24 }}>
    {own
      ? <Link component="button" type="button" variant="caption" onClick={onReset}>Reset</Link>
      : <Typography variant="caption" color="text.secondary">{caption}</Typography>}
  </Box>
);

const FieldRow: React.FC<{
  own: boolean;
  origin: StyleOrigin;
  onReset: () => void;
  children: React.ReactNode;
}> = ({ own, origin, onReset, children }) => (
  <Box>
    {children}
    <FieldFooter own={own} caption={originCaption[origin]} onReset={onReset} />
  </Box>
);

const ShadeRow: React.FC<{
  label: string;
  own: boolean;
  value: boolean;
  onChange: (value: boolean) => void;
  onReset: () => void;
}> = ({ label, own, value, onChange, onReset }) => (
  <Box>
    <FormControlLabel
      control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} />}
      label={label}
    />
    <FieldFooter own={own} caption="Inherited" onReset={onReset} />
  </Box>
);


interface TagEditorProps {
  registry: TagRegistry;
  writableCategories: TagCategory[];
  defaults: DefaultStyles;
  scopeUserId: number | null;
  editing: Tag | null;
  initialCategoryId: number | null;
  initialParentId: number | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (msg: string) => void;
}

const TagEditor: React.FC<TagEditorProps> = ({
  registry, writableCategories, defaults, scopeUserId, editing,
  initialCategoryId, initialParentId, onClose, onSaved, onError,
}) => {
  const [name, setName] = useState(editing?.tag ?? '');
  const [sortOrder, setSortOrder] = useState<number | null>(editing?.sort_order ?? null);
  const [categoryId, setCategoryId] = useState<number | null>(editing ? editing.category_id : initialCategoryId);
  const [parentId, setParentId] = useState<number | null>(editing ? editing.parent_tag_id : initialParentId);
  const [path, setPath] = useState<PathStyle>(editing?.path_style ?? {});
  const [marker, setMarker] = useState<MarkerStyle>(editing?.marker_style ?? {});

  const moving = useMemo(() => (editing ? [editing] : []), [editing]);
  const categoryTags = useMemo(
    () => (categoryId == null ? [] : Array.from(registry.tags.values()).filter(t => t.category_id === categoryId)),
    [registry, categoryId],
  );
  const options = useMemo(() => flattenTagTree(categoryTags), [categoryTags]);
  const reasons = useMemo(() => parentReasons(categoryTags, moving, registry), [categoryTags, moving, registry]);

  const parentTag = parentId == null ? null : registry.tags.get(parentId) ?? null;
  const currentProblem = parentProblem(parentTag, moving, registry);
  const rootProblem = parentProblem(null, moving, registry);

  const draft: Tag = {
    id: editing?.id ?? -1,
    category_id: categoryId ?? -1,
    parent_tag_id: parentId,
    tag: name,
    sort_order: sortOrder ?? 0,
    path_style: path,
    marker_style: marker,
  };
  const placed = categoryId == null ? registry : withPlacedTag(registry, draft, sortOrder);
  const resolved = resolveStyleDetail(placed.tags.get(draft.id) ?? draft, placed, defaults);
  const pathType = resolved.path.type ?? 'solid';
  const markerType = resolved.marker.type ?? 'circle';

  const trimmed = name.trim();
  const canSave = trimmed !== '' && categoryId != null && currentProblem === null;

  const save = async () => {
    if (!canSave || categoryId == null) return;
    try {
      if (editing) {
        await updateTag(
          editing.id,
          { tag: trimmed, sort_order: sortOrder, parent_tag_id: parentId, path_style: path, marker_style: marker },
          { scopeUserId },
        );
      } else {
        await createTag(
          { category_id: categoryId, parent_tag_id: parentId, tag: trimmed, sort_order: sortOrder, path_style: path, marker_style: marker },
          scopeUserId,
        );
      }
      await onSaved();
    } catch (err) { onError((err as Error).message); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? 'Edit Tag' : 'New Tag'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField label="Sort Order" type="number" value={sortOrder ?? ''} placeholder="Last"
            InputLabelProps={{ shrink: true }} fullWidth
            onChange={(e) => setSortOrder(e.target.value === '' ? null : Math.max(0, Math.trunc(Number(e.target.value))))} />

          <FormControl fullWidth disabled={!!editing}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={categoryId == null ? '' : String(categoryId)}
              onChange={(e) => { setCategoryId(Number(e.target.value)); setParentId(null); }}
            >
              {writableCategories.map(c => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.tag_category}{c.scope === 'global' ? ' (Global)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth error={currentProblem !== null}>
            <InputLabel>Parent</InputLabel>
            <Select
              label="Parent"
              value={parentId == null ? ROOT : String(parentId)}
              onChange={(e) => setParentId(e.target.value === ROOT ? null : Number(e.target.value))}
            >
              <MenuItem value={ROOT} disabled={rootProblem !== null}>(Root)</MenuItem>
              {options.map(({ tag, depth }) => (
                <MenuItem
                  key={tag.id}
                  value={String(tag.id)}
                  disabled={parentProblem(tag, moving, registry) !== null}
                  sx={{ pl: 2 + depth * 2 }}
                >
                  {tag.tag}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{reasons.join(' ') || ' '}</FormHelperText>
          </FormControl>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Preview</Typography>
            <Box sx={{
              display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center',
              height: 56, border: 1, borderColor: 'divider', borderRadius: 1,
            }}>
              {pathType !== 'none' && (
                <svg width={96} height={24} aria-hidden="true">
                  <line
                    x1={4} y1={12} x2={92} y2={12}
                    stroke={resolved.path.color ?? '#000000'}
                    strokeOpacity={resolved.path.opacity ?? 1}
                    strokeWidth={resolved.path.weight ?? 3}
                    strokeDasharray={dashArrayFor(pathType, resolved.path.dash)}
                  />
                </svg>
              )}
              {markerType !== 'none' && (
                <MarkerShape
                  type={markerType}
                  size={Math.max(8, 2 * (resolved.marker.radius ?? 4) + 2)}
                  fill={resolved.marker.fillColor ?? '#000000'}
                  fillOpacity={resolved.marker.fillType === 'none' ? 0 : (resolved.marker.fillOpacity ?? 0.2)}
                  stroke={resolved.marker.borderColor ?? '#000000'}
                  strokeWidth={resolved.marker.borderType === 'none' ? 0 : (resolved.marker.borderWeight ?? 1)}
                  strokeOpacity={resolved.marker.borderOpacity ?? 1}
                  dashArray={resolved.marker.borderType === 'dashed' ? (resolved.marker.borderDash ?? '5,5') : undefined}
                />
              )}
            </Box>
          </Box>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Path Style</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FieldRow own={path.type !== undefined} origin={resolved.origins.path.type}
                  onReset={() => setPath(p => withField(p, 'type', undefined))}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select label="Type" value={pathType}
                      onChange={(e) => setPath(p => withField(p, 'type', e.target.value as PathStyle['type']))}>
                      <MenuItem value="solid">Solid</MenuItem>
                      <MenuItem value="dashed">Dashed</MenuItem>
                      <MenuItem value="dotted">Dotted</MenuItem>
                      <MenuItem value="dash-dot">Dash-Dot</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                    </Select>
                  </FormControl>
                </FieldRow>

                {pathType !== 'none' && (
                  <>
                    <FieldRow own={path.color !== undefined} origin={resolved.origins.path.color}
                      onReset={() => setPath(p => withField(p, 'color', undefined))}>
                      <TextField label="Color" type="color" fullWidth InputLabelProps={{ shrink: true }}
                        value={resolved.path.color ?? '#000000'}
                        onChange={(e) => setPath(p => withField(p, 'color', e.target.value))} />
                    </FieldRow>

                    <FieldRow own={path.opacity !== undefined} origin={resolved.origins.path.opacity}
                      onReset={() => setPath(p => withField(p, 'opacity', undefined))}>
                      <Typography variant="body2">Opacity</Typography>
                      <Slider min={0} max={1} step={0.05} value={resolved.path.opacity ?? 1}
                        onChange={(_, v) => setPath(p => withField(p, 'opacity', v as number))}
                        valueLabelDisplay="auto" />
                    </FieldRow>

                    <FieldRow own={path.weight !== undefined} origin={resolved.origins.path.weight}
                      onReset={() => setPath(p => withField(p, 'weight', undefined))}>
                      <Typography variant="body2">Weight</Typography>
                      <Slider min={0} max={20} step={1} value={resolved.path.weight ?? 3}
                        onChange={(_, v) => setPath(p => withField(p, 'weight', v as number))}
                        valueLabelDisplay="auto" />
                    </FieldRow>
                  </>
                )}

                <ShadeRow label="Shade Children" own={path.shadeChildren !== undefined} value={resolved.shade.path}
                  onChange={(v) => setPath(p => withField(p, 'shadeChildren', v))}
                  onReset={() => setPath(p => withField(p, 'shadeChildren', undefined))} />

                <ShadeRow label="Aggregate Points" own={path.aggregate !== undefined} value={resolved.path.aggregate === true}
                  onChange={(v) => setPath(p => withField(p, 'aggregate', v))}
                  onReset={() => setPath(p => withField(p, 'aggregate', undefined))} />
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Marker Style</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FieldRow own={marker.type !== undefined} origin={resolved.origins.marker.type}
                  onReset={() => setMarker(m => withField(m, 'type', undefined))}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select label="Type" value={markerType}
                      onChange={(e) => setMarker(m => withField(m, 'type', e.target.value as MarkerStyle['type']))}>
                      <MenuItem value="circle">Circle</MenuItem>
                      <MenuItem value="square">Square</MenuItem>
                      <MenuItem value="triangle">Triangle</MenuItem>
                      <MenuItem value="diamond">Diamond</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                    </Select>
                  </FormControl>
                </FieldRow>

                {markerType !== 'none' && (
                  <>
                    <FieldRow own={marker.radius !== undefined} origin={resolved.origins.marker.radius}
                      onReset={() => setMarker(m => withField(m, 'radius', undefined))}>
                      <Typography variant="body2">Radius</Typography>
                      <Slider min={0} max={20} step={1} value={resolved.marker.radius ?? 4}
                        onChange={(_, v) => setMarker(m => withField(m, 'radius', v as number))}
                        valueLabelDisplay="auto" />
                    </FieldRow>

                    <FieldRow own={marker.fillColor !== undefined} origin={resolved.origins.marker.fillColor}
                      onReset={() => setMarker(m => withField(m, 'fillColor', undefined))}>
                      <TextField label="Fill Color" type="color" fullWidth InputLabelProps={{ shrink: true }}
                        value={resolved.marker.fillColor ?? '#000000'}
                        onChange={(e) => setMarker(m => withField(m, 'fillColor', e.target.value))} />
                    </FieldRow>

                    <FieldRow own={marker.borderColor !== undefined} origin={resolved.origins.marker.borderColor}
                      onReset={() => setMarker(m => withField(m, 'borderColor', undefined))}>
                      <TextField label="Border Color" type="color" fullWidth InputLabelProps={{ shrink: true }}
                        value={resolved.marker.borderColor ?? '#000000'}
                        onChange={(e) => setMarker(m => withField(m, 'borderColor', e.target.value))} />
                    </FieldRow>
                  </>
                )}

                <ShadeRow label="Shade Children" own={marker.shadeChildren !== undefined} value={resolved.shade.marker}
                  onChange={(v) => setMarker(m => withField(m, 'shadeChildren', v))}
                  onReset={() => setMarker(m => withField(m, 'shadeChildren', undefined))} />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!canSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagEditor;
